import { ModalFooter } from 'react-bootstrap';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';

import Button from 'components/button/Button';
import ModalFormCancelButton from 'components/modal/form/ModalFormCancelButton';
import ModalFormSubmitButton from 'components/modal/form/ModalFormSubmitButton';
import { LanguageCode } from 'i18n/internationalization.types';
import { SystemTemplate } from 'models/automation/SystemTemplates';
import { SystemTemplatesModalFooter } from '../../SystemTemplatesTab.styles';
import useSystemTemplateResetModal from '../reset/useSystemTemplateResetModal';
import { SystemTemplateEditModalFormValues } from './SystemTemplateEditModal.schema';

interface SystemTemplateEditModalFooterProps {
  systemTemplate: SystemTemplate;
  closeModal: () => void;
}

const SystemTemplateEditModalFooter = ({
  systemTemplate,
  closeModal,
}: SystemTemplateEditModalFooterProps) => {
  const { t } = useTranslation();
  const { control, reset } =
    useFormContext<SystemTemplateEditModalFormValues>();
  const { defaultValues, isDirty, isSubmitting } = useFormState();
  const currentLanguage = useWatch({ control, name: 'language' });
  const { openSystemTemplateResetModal } = useSystemTemplateResetModal();

  return (
    <ModalFooter>
      <SystemTemplatesModalFooter>
        <Button
          bsStyle="danger"
          data-testid="modal-form-reset-button"
          onClick={() => {
            openSystemTemplateResetModal({
              key: systemTemplate.key,
              languageCode: currentLanguage as LanguageCode,
              onAfterConfirm: () => {
                // We need to discard user changes as otherwise the dirty check
                // modal would appear when calling `closeModal`.
                flushSync(() => reset(defaultValues, { keepDirty: false }));
                closeModal();
              },
            });
          }}
        >
          {t('pageAutomations.systemTemplates.modalReset.title')}
        </Button>

        <ModalFormCancelButton closeOnClick={false} onClick={closeModal} />

        <ModalFormSubmitButton disabled={!isDirty}>
          {isSubmitting
            ? t('common.saving')
            : t('pageAutomations.systemTemplates.modal.saveTemplate')}
        </ModalFormSubmitButton>
      </SystemTemplatesModalFooter>
    </ModalFooter>
  );
};

export default SystemTemplateEditModalFooter;
